import {compact, isString} from 'lodash';
import {Key, ReactNode} from 'react';

import {defineMessage} from '../../../../../libs/shared/helpers/src/internationalization/message';
import {ModifiersEnum} from '../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {SearchHintModel, SearchHintTypesEnum, SearchHintValue} from '../../../../models/api/searchHintModel';
import {InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {InboxesSearchParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  SearchLocalFilterIconType,
  SearchLocalFilterTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';

export interface VisualSearchHintValue<T extends string = string> extends SearchHintValue {
  value: T;
  isSelected: boolean;
  icon?: SearchLocalFilterIconType;
  tooltip?: ReactNode;
  key: Key;
}

export interface VisualSearchHint<T extends string = string> extends SearchHintModel {
  values: ReadonlyArray<VisualSearchHintValue<T>>;
  isEnabled: boolean;
  onClick?: (
    hint: VisualSearchHint<T>,
    selectedValue: VisualSearchHintValue<T>,
  ) => Partial<InboxesSearchParameters>;
  isSingleChoice?: boolean;
  title?: string;
  additionalIcon?: SearchLocalFilterIconType;
  key: Key;
}

export const serializeToVisualHint = (
  hint: SearchHintModel,
  searchQuery: string,
  localize: Localizer,
): VisualSearchHint | undefined => {
  const {type, values} = hint;

  const visualHintValues: ReadonlyArray<VisualSearchHintValue> = values.map((hintValue) => ({
    ...hintValue,
    isSelected: Boolean(
      hintValue.modifier && searchQuery.includes(`${hintValue.modifier}:${hintValue.value}`),
    ),
    key: `${hintValue.modifier}-${hintValue.value}`,
  }));

  const isEnabled = visualHintValues.some(({isSelected}) => isSelected);

  switch (type) {
    case SearchHintTypesEnum.ATTACHMENT:
      return {
        ...hint,
        values: visualHintValues,
        isEnabled,
        priority: -2,
        fallbackTitle: localize(
          defineMessage({
            id: 'search-hint-has-attachment',
            message: 'Has attachment',
          }),
        ),
        additionalIcon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'attachmentSmall',
          selectedIconName: 'attachmentSmall',
        },
        key: type,
      };

    case SearchHintTypesEnum.RECIPIENT:
      return {
        ...hint,
        title: hint.fallbackTitle,
        values: visualHintValues,
        isEnabled,
        priority: -1,
        additionalIcon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'participantSmall',
          selectedIconName: 'participantFilledSmall',
        },
        key: type,
      };

    case SearchHintTypesEnum.SENT_BY_ME:
      return {
        ...hint,
        values: visualHintValues,
        isEnabled,
        priority: -4,
        fallbackTitle: localize(
          defineMessage({
            id: 'search-hint-sent-by-me',
            message: 'Author me',
          }),
        ),
        additionalIcon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'participantSmall',
          selectedIconName: 'participantFilledSmall',
        },
        key: type,
      };

    case SearchHintTypesEnum.UNREAD:
      return {
        ...hint,
        values: visualHintValues,
        isEnabled,
        priority: -3,
        fallbackTitle: localize(
          defineMessage({
            id: 'search-hint-unread',
            message: 'Is Unread',
          }),
        ),
        additionalIcon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'eyeCrossedSmall',
          selectedIconName: 'eyeCrossedFilledSmall',
        },
        key: type,
      };

    case SearchHintTypesEnum.MODIFIER:
      return serializeModifierHintToVisualHint(hint, searchQuery, localize);

    case SearchHintTypesEnum.INCLUDE_CALENDAR_UPDATES:
      return {
        ...hint,
        values: visualHintValues,
        isEnabled,
        priority: 0,
        fallbackTitle: localize(
          defineMessage({
            id: 'search-hint-include-calendar-updates',
            message: 'w/ calendar updates',
          }),
        ),
        additionalIcon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'calendarSmall',
          selectedIconName: 'calendarFilledSmall',
        },
        key: type,
      };

    // This may happen if there are new hint types on the BE that we did not implement on the FE yet.
    default:
      return {
        ...hint,
        values: visualHintValues.map((val) => ({
          ...val,
          icon: {
            type: SearchLocalFilterTypesEnum.ICON,
            iconName: 'helpFilledSmall',
          },
        })),
        isEnabled,
        key: type,
      };
  }
};

// @TODO: Replace `serializeToVisualHint` with `serializeModifierHintToVisualHint` once we only return modifier hints.
const serializeModifierHintToVisualHint = (
  hint: SearchHintModel,
  searchQuery: string,
  localize: Localizer,
): VisualSearchHint | undefined => {
  const {values} = hint;

  const visualHintValues: ReadonlyArray<VisualSearchHintValue> = compact(
    values.map((hintValue, index) => {
      const isSelected = Boolean(
        hintValue.modifier &&
          searchQuery.includes(`${hintValue.modifier}:${maybeWrapInDoubleQuotes(hintValue.value)}`),
      );

      // Remove values that do not appear in the query.
      // @TODO: Remove this once we only return modifier hints (as we need to display non-selected values).
      if (!isSelected) {
        return undefined;
      }

      return {
        ...hintValue,
        ...computeTitleAndIconForHintValue(hintValue, localize),
        isSelected: true,
        // There's no guarantee `<modifier>:<value>` would be unique (e.g. for a tag with the same alias in multiple namespaces).
        // Using index in the key is acceptable here, because a value is not the same if its position changed within `values`.
        key: `${hintValue.modifier}-${hintValue.value}-${index}`,
      };
    }),
  );

  const isEnabled = visualHintValues.length > 0;

  // If there's no selected value, return early.
  // @TODO: Remove this once we only return modifier hints (as we need to display non-selected hints).
  if (!isEnabled) {
    return undefined;
  }

  // At this point, we're sure `visualHintValues` has at least one item.
  const [{modifier, value}] = visualHintValues;

  return {
    ...hint,
    values: visualHintValues,
    isEnabled,
    title: modifier && computeTitleForHint(modifier, visualHintValues, localize),
    additionalIcon: modifier && computeAdditionalIconForHint(modifier, visualHintValues.length),
    key: `${modifier}-${value}`,
  };
};

const computeTitleAndIconForHintValue = (
  hintValue: SearchHintValue,
  localize: Localizer,
): Pick<VisualSearchHintValue, 'title' | 'icon'> => {
  const {modifier, title, value} = hintValue;
  const displayedValue = title ?? maybeWrapInDoubleQuotes(value);

  switch (modifier) {
    case ModifiersEnum.ACCOUNT:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-account',
            message: `Account ${displayedValue}`,
          }),
        ),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'briefcaseOutlineSmall',
          selectedIconName: 'briefcaseFilledSmall',
        },
      };

    case ModifiersEnum.AUTHOR:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-author',
            message: `Author ${displayedValue}`,
          }),
        ),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'participantSmall',
          selectedIconName: 'participantFilledSmall',
        },
      };

    case ModifiersEnum.BODY:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-body',
            message: `Body contains ${displayedValue}`,
          }),
        ),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'contentSmall',
          selectedIconName: 'contentSmall',
        },
      };

    case ModifiersEnum.FROM:
      return {
        title: displayedValue,
      };

    case ModifiersEnum.HAS: {
      switch (value) {
        case 'attachments':
        case 'attachment':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-has-attachment',
                message: 'Has attachment',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'attachmentSmall',
              selectedIconName: 'attachmentSmall',
            },
          };

        case 'reminder':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-has-reminder',
                message: 'Has reminder',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'timeSmall',
              selectedIconName: 'timeFilledSmall',
            },
          };

        case 'no-tags':
        case 'no-tag':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-has-no-tags',
                message: 'Has no tags',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'noTagSmall',
              selectedIconName: 'tagIsNotFilledSmall',
            },
          };

        default:
          return {
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'helpFilledSmall',
            },
          };
      }
    }

    case ModifiersEnum.ID:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-id',
            message: `Id ${displayedValue}`,
          }),
        ),
      };

    case ModifiersEnum.IS:
      switch (value) {
        case 'archived':
        case 'closed':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-archived',
                message: 'Is archived',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'archiveSmall',
              selectedIconName: 'archiveFilledSmall',
            },
          };

        case 'assigned':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-assigned',
                message: 'Is assigned',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'assignSmall',
              selectedIconName: 'assignFilledSmall',
            },
          };

        case 'discussion':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-discussion',
                message: 'Is discussion',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'discussionSmall',
              selectedIconName: 'discussionFilledSmall',
            },
          };

        case 'unsubscribed':
        case 'muted':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unsubscribed',
                message: 'Is unsubscribed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'bellCrossedSmall',
              selectedIconName: 'bellCrossedSmall',
            },
          };

        case 'open':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-open',
                message: 'Is open',
              }),
            ),
          };

        case 'shared-with-me':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-shared-with-me',
                message: 'Is shared with me',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'teammatesSmall',
              selectedIconName: 'sharedFilledSmall',
            },
          };

        case 'snoozed':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-snoozed',
                message: 'Is snoozed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'snoozeSmall',
              selectedIconName: 'snoozeFilledSmall',
            },
          };

        case 'spam':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-spam',
                message: 'Is spam',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'spamSmall',
              selectedIconName: 'spamFilledSmall',
            },
          };

        case 'starred':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-starred',
                message: 'Is starred',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'starSmall',
              selectedIconName: 'starFilledSmall',
            },
          };

        case 'subscribed':
        case 'following':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-subscribed',
                message: 'Is subscribed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'subscribeSmall',
              selectedIconName: 'bellSmall',
            },
          };

        case 'trashed':
        case 'deleted':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-trashed',
                message: 'Is trashed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'trashSmall',
              selectedIconName: 'trashFilledSmall',
            },
          };

        case 'unassigned':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unassigned',
                message: 'Is unassigned',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'unassignedSmall',
              selectedIconName: 'unassignedFilledSmall',
            },
          };

        case 'unread':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unread',
                message: 'Is unread',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'eyeCrossedSmall',
              selectedIconName: 'eyeCrossedFilledSmall',
            },
          };

        case 'unreplied':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unreplied',
                message: 'Is unreplied',
              }),
            ),
          };

        default:
          return {
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'helpFilledSmall',
            },
          };
      }

    case ModifiersEnum.SUBJECT:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-subject',
            message: `Subject contains ${displayedValue}`,
          }),
        ),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'contentSmall',
          selectedIconName: 'contentSmall',
        },
      };

    case ModifiersEnum.TAG:
      return {
        title: displayedValue,
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'tagSmall',
          selectedIconName: 'tagFilledSmall',
        },
      };

    case ModifiersEnum.TO:
      return {
        title: displayedValue,
      };

    default:
      return {
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'helpFilledSmall',
        },
      };
  }
};

export const maybeWrapInDoubleQuotes = (value: string) => (/\s/.test(value) ? `"${value}"` : value);

const computeTitleForHint = (
  modifier: ModifiersEnum,
  selectedValues: ReadonlyArray<VisualSearchHintValue>,
  localize: Localizer,
): string | undefined => {
  switch (modifier) {
    case ModifiersEnum.FROM: {
      if (selectedValues.length > 1) {
        return localize(
          defineMessage({
            id: 'search-hint-from-multiple',
            message: `From ${selectedValues.map(({title}) => maybeTruncateValue(title, 11)).join(' or ')}`,
          }),
        );
      }

      const [{title}] = selectedValues;
      return localize(
        defineMessage({
          id: 'search-hint-from',
          message: `From ${title}`,
        }),
      );
    }

    case ModifiersEnum.TO: {
      if (selectedValues.length > 1) {
        return localize(
          defineMessage({
            id: 'search-hint-to-multiple',
            message: `To ${selectedValues.map(({title}) => maybeTruncateValue(title, 11)).join(' or ')}`,
          }),
        );
      }

      const [{title}] = selectedValues;
      return localize(
        defineMessage({
          id: 'search-hint-to',
          message: `To ${title}`,
        }),
      );
    }

    case ModifiersEnum.TAG: {
      if (selectedValues.length > 1) {
        // If we have multiple selected values, it means we matched a tag that has the same alias across multiple namespaces.
        // Only return the name of the tag once.
        return selectedValues[0].title;
      }

      return undefined;
    }

    default:
      return undefined;
  }
};

export const maybeTruncateValue = (value: ReactNode, maxChars: number = 20) => {
  if (!isString(value)) {
    return value;
  }

  if (value.length <= maxChars) {
    return value;
  }

  return `${value.slice(0, maxChars)}…`;
};

const computeAdditionalIconForHint = (
  modifier: ModifiersEnum,
  selectedValuesCount: number,
): SearchLocalFilterIconType | undefined => {
  switch (modifier) {
    case ModifiersEnum.FROM:
    case ModifiersEnum.TO:
      return {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'participantSmall',
        selectedIconName: 'participantFilledSmall',
      };

    case ModifiersEnum.TAG: {
      if (selectedValuesCount > 1) {
        return {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'tagsSmall',
          selectedIconName: 'tagsFilledSmall',
        };
      }

      return undefined;
    }

    default:
      return undefined;
  }
};

export const makeSearchFiltersVisualHint = (
  selectedSearchFilter: InboxesSearchLocationsEnum,
  localSearchFilterName: ReactNode,
  localSearchFilterIcon: SearchLocalFilterIconType,
  localize: Localizer,
  hasPreventPinTeamTags: boolean,
): VisualSearchHint<InboxesSearchLocationsEnum> => {
  const values: ReadonlyArray<VisualSearchHintValue<InboxesSearchLocationsEnum>> = [
    {
      value: InboxesSearchLocationsEnum.LOCAL,
      isSelected: selectedSearchFilter === InboxesSearchLocationsEnum.LOCAL,
      // @TODO: Remove cast.
      title: localSearchFilterName as string,
      icon: localSearchFilterIcon,
      key: InboxesSearchLocationsEnum.LOCAL,
    },
    {
      value: InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
      isSelected: selectedSearchFilter === InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
      title: localize(
        defineMessage({
          id: 'search-hint-filter-sidebar',
          message: 'My sidebar',
        }),
      ),
      icon: {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'workspaceSmall',
        selectedIconName: 'workspaceSmall',
      },
      tooltip: hasPreventPinTeamTags
        ? localize(
            defineMessage({
              id: 'search-hint-filter-sidebar-tooltip-prevent-pin-team-tags',
              message: "Show me results from inboxes, private tags, and views that I've added to my sidebar.",
            }),
          )
        : localize(
            defineMessage({
              id: 'search-hint-filter-sidebar-tooltip',
              message:
                "Show me results from inboxes, tags, views, and teammates that I've added to my sidebar.",
            }),
          ),
      key: InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
    },
    {
      value: InboxesSearchLocationsEnum.GLOBAL,
      isSelected: selectedSearchFilter === InboxesSearchLocationsEnum.GLOBAL,
      title: localize(
        defineMessage({
          id: 'search-hint-filter-all-inboxes',
          message: 'All inboxes',
        }),
      ),
      icon: {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'inboxSmall',
        selectedIconName: 'inboxFilledSmall',
      },
      tooltip: hasPreventPinTeamTags
        ? localize(
            defineMessage({
              id: 'search-hint-filter-all-inboxes-tooltip-prevent-pin-team-tags',
              message:
                'Show me search results from all inboxes, private tags, and views that I have access to.',
            }),
          )
        : localize(
            defineMessage({
              id: 'search-hint-filter-all-inboxes-tooltip',
              message:
                'Show me search results from all inboxes, tags, views, and teammates that I have access to.',
            }),
          ),
      key: InboxesSearchLocationsEnum.GLOBAL,
    },
  ];

  return {
    type: SearchHintTypesEnum.SEARCH_FILTERS,
    values,
    isEnabled: values.some(({isSelected}) => isSelected),
    priority: 1,
    onClick: (hint, selectedValue) => ({location: selectedValue.value}),
    isSingleChoice: true,
    key: SearchHintTypesEnum.SEARCH_FILTERS,
  };
};
